import Modal from 'src/components/ui/Modal'
import React, { useContext, useEffect, useState } from 'react'
import Select from 'react-select'
import TextField from 'src/components/ui/TextField'
import AddBtnIcon from 'src/components/icons/ui/AddBtn'
import RemoveBtnIcon from 'src/components/icons/ui/RemoveBtn'
import UIMessages from 'src/utils/ui-messages'
import { ITripGroupRides } from 'src/types/trip-group-rides'
import { serviceEntityService } from 'src/services/Service'
import { paymentMethodsService } from 'src/services/PaymentMethods'
import { tripService } from 'src/services/Trip'
import { NotificationProvider } from 'src/components/ui/notification-provider'
import * as tripActions from '../../../../state/actions/trips'
import { useDispatch } from 'react-redux'
import { PcaConfirmationModal } from '../../modal/SetTripPassengerModal'

export interface IUpdatePassengerDetailsModalProps {
  tripsGroupRides: ITripGroupRides[]
  setTripGroupRides: (value: ITripGroupRides[]) => void
  setIsConfirmOpen: (value: boolean) => void
  setIsModalOpen: (value: boolean) => void
  serviceId: string
  tripId: string
  operatorId?: string
}

export interface ISelectValue {
  value: string
  label: string
}

export interface IPassengerDetailsPayload {
  tripId: string
  passengerDetails: ITripGroupRides[]
}

const defaultPaymentMethods = [
  { value: 'CreditCard', label: 'Credit Card' },
  { value: 'Cash', label: 'Cash' },
  { value: 'Wallet', label: 'Wallet' },
  { value: 'Orca', label: 'Orca' }
]

const defaultRiderOptions = [
  { value: 'RRFP Cardholders', label: 'Senior' },
  { value: 'Adult', label: 'Adult' },
  { value: 'Youth', label: 'Youth' },
  { value: 'Children', label: 'Children' }
]

const initialPassengerDetails = {
  id: '',
  tripId: '',
  riderType: '',
  fareType: '',
  amount: 0,
  orcaNumber: 0,
  totalRider: 0,
  totalAmount: 0
}

const UpdatePassengerDetailsModal = (props: IUpdatePassengerDetailsModalProps) => {
  const { tripsGroupRides, setIsModalOpen, setIsConfirmOpen, serviceId, setTripGroupRides, tripId, operatorId } = props

  const notification: any = useContext(NotificationProvider)

  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [passengerDetails, setPassengerDetails] = useState<ITripGroupRides[]>(tripsGroupRides)
  const [paymentMethods, setPaymentMethods] = useState<ISelectValue[]>(defaultPaymentMethods)
  const [riderOptions, setRiderOptions] = useState<ISelectValue[]>(defaultRiderOptions)

  const dispatch = useDispatch()

  useEffect(() => {
    retrievePaymentMethods()
    retrieveRiderTypes()
  }, [])

  const addPassengerDetails = () => {
    const array = JSON.parse(JSON.stringify(passengerDetails))
    array.push(initialPassengerDetails)
    setPassengerDetails(array)
  }

  const removePassengerDetails = (index: number) => {
    const array = JSON.parse(JSON.stringify(passengerDetails))
    array.splice(index, 1)
    setPassengerDetails(array)
  }

  const updatePassengerDetails = (index: number, value: any, type: string) => {
    const array = JSON.parse(JSON.stringify(passengerDetails))

    const label = value.label !== undefined ? value.label : value

    if (type === 'riderType') {
      array[index].riderType = label
      if (label === 'PCA') {
        array[index].fareType = 'Free'
        setConfirmModalOpen(true)
      } else {
        array[index].fareType = paymentMethods[0].value
        setConfirmModalOpen(false)
      }
      setPassengerDetails(array)
    } else if (type === 'fareType') {
      array[index].fareType = label
      setPassengerDetails(array)
    } else if (type === 'totalRider') {
      array[index].totalRider = (label as unknown) as number
      setPassengerDetails(array)
    } else if (type === 'amount') {
      array[index].amount = (label as unknown) as number
      setPassengerDetails(array)
    }
  }

  const retrieveRiderTypes = async () => {
    const response = await serviceEntityService.fetchServicePricingType(serviceId)

    const riderTypes = response.data.map((data: any) => {
      const riderType = {
        value: data.riderType,
        label: data.riderType
      }

      return riderType
    })

    setRiderOptions(riderTypes)
  }

  const retrievePaymentMethods = async () => {
    const response = await paymentMethodsService.fetchPaymentMethods(serviceId)

    if (response !== null) {
      const paymentMethods = response
        .filter(data => data.activated === true)
        .map(data => {
          const paymentMethod = {
            value: data.paymentName,
            label: data.paymentName
          }

          return paymentMethod
        })
      setPaymentMethods(paymentMethods)
    }
  }

  const getTripById = async () => {
    return dispatch(tripActions.getTripById(tripId))
  }

  const findInvalidTripGroupRide = (passengerDetails: ITripGroupRides[]) => {
    const paymentMethodLabels = paymentMethods.map(method => method.label)
    const riderOptionLabels = riderOptions.map(option => option.label)
    passengerDetails.map((passengerDetail, index) => {
      if (
        !paymentMethodLabels.includes(passengerDetail.fareType) ||
        !riderOptionLabels.includes(passengerDetail.riderType)
      ) {
        passengerDetails[index].error = true
      } else {
        passengerDetails[index].error = false
      }
    })
    setPassengerDetails(passengerDetails)
  }

  const handleSubmit = async () => {
    const array = JSON.parse(JSON.stringify(passengerDetails))
    const filteredPassengerDetails: ITripGroupRides[] = array.filter((passengerDetail: ITripGroupRides) => {
      return passengerDetail.fareType !== '' && passengerDetail.riderType !== '' && passengerDetail.totalRider !== 0
    })

    const payload: IPassengerDetailsPayload = {
      tripId,
      passengerDetails: filteredPassengerDetails
    }

    const response: any = await tripService.updatePassengerDetails(payload)

    if (response.data.success === true) {
      notification.addNotification({
        title: 'Success!',
        level: 'success',
        message: 'Passenger details successfully updated!'
      })
      const passengerDetailsWithNoErrors: ITripGroupRides[] = filteredPassengerDetails.map(passengerDetail => {
        const item = {
          id: '',
          tripId,
          riderType: passengerDetail.riderType,
          fareType: passengerDetail.fareType,
          amount: passengerDetail.amount,
          orcaNumber: passengerDetail.orcaNumber,
          totalRider: passengerDetail.totalRider,
          totalAmount: passengerDetail.totalAmount
        }

        return item
      })

      // Send push notification to operator when PCA rider type is newly added
      const isInitialDetailHasPCA = tripsGroupRides.filter(({ riderType }) => riderType === 'PCA')
      const isUpdatedDetailsHasPCA = filteredPassengerDetails.filter(({ riderType }) => riderType === 'PCA')
      if (isInitialDetailHasPCA.length === 0 && isUpdatedDetailsHasPCA.length > 0 && operatorId) {
        dispatch(
          tripActions.sendPushNotificationToDriver({
            operatorId,
            title: `GOIN' Updates`,
            body: 'PCA rider is included, and no payment is required.'
          })
        )
      }

      setTripGroupRides(passengerDetailsWithNoErrors)
      setPassengerDetails(passengerDetailsWithNoErrors)
      setIsModalOpen(false)
      setIsConfirmOpen(true)
      await getTripById()
    } else {
      findInvalidTripGroupRide(filteredPassengerDetails)
      notification.addNotification({
        title: 'Error!',
        level: 'error',
        message:
          response.data && response.data.message
            ? response.data.message
            : 'An error occured while saving your changes. Please try again.'
      })
    }
  }

  return (
    <Modal>
      <div className='fixed top-0 bottom-0 left-0 right-0 flex justify-center items-center'>
        <div className='main-modal'>
          <div className='font-grey2 weight-500 modal-title'>UPDATE PASSENGER DETAILS</div>
          <div className='modal-body'>
            <div className='passenger-details-header'>
              <div className='col-3'>Rider</div>
              <div className='col-3'>Method</div>
              <div className='col-2 pr1'>No.</div>
              <div className='col-2'>Collected</div>
              <div className='col-2' />
            </div>
            <div className='passenger-details-body'>
              {passengerDetails.map((passengerDetail, index) => {
                return (
                  <div className='passenger-details-item' key={index} id={`${index}`}>
                    <div className='col-3 pr1'>
                      <Select
                        placeholder='Select Rider'
                        menuPosition='fixed'
                        options={riderOptions}
                        value={{
                          value: passengerDetail.riderType,
                          label: passengerDetail.riderType
                        }}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: passengerDetail.error === true ? 'red' : '#CCCCCC'
                          })
                        }}
                        onChange={value => updatePassengerDetails(index, value ? value : '', 'riderType')}
                      />
                    </div>
                    <div className='col-3 pr1'>
                      <Select
                        isDisabled={passengerDetail.riderType === 'PCA'}
                        placeholder='Select Method'
                        menuPosition='fixed'
                        options={paymentMethods}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: passengerDetail.error === true ? 'red' : '#CCCCCC'
                          })
                        }}
                        value={{
                          value: passengerDetail.fareType,
                          label: passengerDetail.fareType
                        }}
                        onChange={value => updatePassengerDetails(index, value ? value : '', 'fareType')}
                      />
                    </div>
                    <div className='col-2 g-mt0-all pr1'>
                      <TextField
                        className='g-w100 h38'
                        name='No'
                        type='number'
                        min={0}
                        isRed={passengerDetail.error === true}
                        classNameContainer='mt0'
                        onChange={event => updatePassengerDetails(index, event.target.value, 'totalRider')}
                        value={passengerDetail.totalRider.toString()}
                      />
                    </div>
                    <div className='col-2 g-mt0-all pr1'>
                      <TextField
                        disabled={passengerDetail.riderType === 'PCA'}
                        className='g-w100 h38'
                        name='Collected'
                        type='number'
                        min={0}
                        isRed={passengerDetail.error === true}
                        classNameContainer='mt0'
                        onChange={event => updatePassengerDetails(index, event.target.value, 'amount')}
                        value={passengerDetail.amount.toString()}
                      />
                    </div>
                    <div className='col-2'>
                      {index === passengerDetails.length - 1 ? (
                        <>
                          {passengerDetails.length > 1 && (
                            <RemoveBtnIcon
                              onClick={() => removePassengerDetails(index)}
                              className='icon--36x36 cursor--pointer self-end svg-red'
                            />
                          )}
                          <AddBtnIcon
                            onClick={() => addPassengerDetails()}
                            className='icon--36x36 cursor--pointer self-end svg-light-green'
                          />
                        </>
                      ) : (
                        <RemoveBtnIcon
                          onClick={() => removePassengerDetails(index)}
                          className='icon--36x36 cursor--pointer self-end svg-red'
                        />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
            <div className='passenger-details-footer'>
              <button
                type='button'
                className='SaveButton inline-block btn bg-light-grey2 font-grey rounded font-white weight-300 mt-auto mb-auto mr1'
                onClick={() => setIsModalOpen(false)}
              >
                {UIMessages.common.cancel}
              </button>
              <button
                type='button'
                className='SaveButton inline-block btn bg-green rounded font-white weight-300 mt-auto mb-auto'
                onClick={() => handleSubmit()}
              >
                {UIMessages.common.save}
              </button>
            </div>
          </div>
        </div>
        <PcaConfirmationModal isOpen={confirmModalOpen} handleOkClick={open => setConfirmModalOpen(open)} />
      </div>
    </Modal>
  )
}

export default UpdatePassengerDetailsModal



// WEBPACK FOOTER //
// ./src/components/trips/trip-detail/update-passenger-details-modal/UpdatePassengerDetailsModal.tsx